import React, { useEffect, useMemo, useState } from "react";
import { calcConfigurator } from "../lib/api";
import ClampViewer3D from "../components/ClampViewer3D";
import { useCart } from "../context/CartContext";
import { useNavigate } from "react-router-dom";
import { ShoppingCart, Wrench, Loader2, Sparkles } from "lucide-react";
import { convertFromMm } from "../lib/conversions";

const MATERIALS = [
  { v: "304", label: "V2A · 304" },
  { v: "316", label: "V4A · 316" },
  { v: "duplex", label: "Duplex" },
  { v: "galvanized", label: "Gegalvaniseerd" },
];
const STRIP_WIDTHS = [20, 25, 30, 40, 50];
const STRIP_THICKS = [3, 4, 5];
const LININGS = [
  { v: "none", label: "Geen" },
  { v: "epdm", label: "EPDM" },
  { v: "silicone", label: "Silicone" },
  { v: "high_temp", label: "Hoge temp" },
];
const FINISHES = [
  { v: "polished", label: "Gepolijst" },
  { v: "brushed", label: "Geborsteld" },
  { v: "mill", label: "Mill" },
];
const MOUNTINGS = ["wand", "plafond", "vloer", "rail"];
const MOUNT_MAP = { "wand": "wall", "plafond": "ceiling", "vloer": "floor", "rail": "rail" };
const LOAD_CLASSES = [
  { v: "light", label: "Licht" },
  { v: "medium", label: "Medium" },
  { v: "heavy", label: "Heavy Duty" },
];
const WELD_OPTS = [
  { v: "standard", label: "Standaard" },
  { v: "welded", label: "Gelast" },
  { v: "reinforced", label: "Verstevigd" },
];

export default function ConfiguratorPage() {
  const [diameter, setDiameter] = useState(33.7);
  const [stripWidth, setStripWidth] = useState(25);
  const [stripThickness, setStripThickness] = useState(3);
  const [material, setMaterial] = useState("304");
  const [finish, setFinish] = useState("polished");
  const [lining, setLining] = useState("none");
  const [mountingLabel, setMountingLabel] = useState("wand");
  const [loadClass, setLoadClass] = useState("medium");
  const [weldOpt, setWeldOpt] = useState("standard");
  const [quantity, setQuantity] = useState(1);
  const [price, setPrice] = useState(null);
  const [loading, setLoading] = useState(false);
  const { addItem } = useCart();
  const navigate = useNavigate();

  const conv = useMemo(() => convertFromMm(diameter), [diameter]);

  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await calcConfigurator({
          diameter_mm: diameter,
          strip_width: stripWidth,
          strip_thickness: stripThickness,
          material,
          finish,
          rubber_lining: lining,
          mounting: MOUNT_MAP[mountingLabel],
          load_class: loadClass,
          weld_option: weldOpt,
          quantity,
        });
        setPrice(res);
      } catch (e) { console.error(e); } finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [diameter, stripWidth, stripThickness, material, finish, lining, mountingLabel, loadClass, weldOpt, quantity]);

  const handleAdd = () => {
    if (!price) return;
    addItem({
      product_id: "custom",
      sku: `CUSTOM-${Math.round(diameter)}-${stripWidth}x${stripThickness}-${material}`,
      name: `Maatwerk beugel Ø${diameter}mm · ${stripWidth}x${stripThickness}mm · ${material.toUpperCase()}`,
      quantity,
      unit_price: price.unit_price,
      image: "https://nibrobv.nl/wp-content/uploads/2020/02/Clamp_DIN3567-scaled.jpg",
      config: { diameter_mm: diameter, strip_width: stripWidth, strip_thickness: stripThickness, material, finish, rubber_lining: lining, mounting: MOUNT_MAP[mountingLabel], load_class: loadClass, weld_option: weldOpt },
    });
    navigate("/cart");
  };

  return (
    <div className="min-h-screen bg-white" data-testid="configurator-page">
      <div className="bg-nibro-sage-pale py-10">
        <div className="mx-auto max-w-[1500px] px-5 lg:px-10">
          <div className="nibro-frame">
            <div className="flex items-center justify-center gap-2">
              <Wrench size={20} className="text-nibro-sage-dark" />
              <h1 className="nibro-section-title">MAATWERK CONFIGURATOR</h1>
            </div>
            <p className="mt-3 text-center text-sm text-zinc-600">
              Stel je eigen pijpbeugel samen. Live 3D-visualisatie. Direct factuurprijs.
            </p>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[1500px] px-5 py-10 lg:px-10">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_1.4fr_320px]">
          {/* LEFT */}
          <div className="space-y-5">
            <Panel title="Diameter & Strip">
              <div>
                <div className="mb-2 flex items-baseline justify-between">
                  <span className="label-uppercase">Buis Ø (D1 mm)</span>
                  <span className="font-mono text-sm font-bold text-zinc-900">{diameter.toFixed(1)} mm</span>
                </div>
                <input
                  type="range" min={10} max={324} step={0.1}
                  value={diameter}
                  onChange={(e) => setDiameter(parseFloat(e.target.value))}
                  className="w-full accent-nibro-sage"
                  data-testid="configurator-diameter-slider"
                />
                <input
                  type="number" min={10} max={400} step={0.1}
                  value={diameter}
                  onChange={(e) => setDiameter(parseFloat(e.target.value) || 10)}
                  className="mt-2 w-full rounded-md border border-zinc-200 bg-white px-3 py-2 font-mono text-sm focus:border-nibro-sage focus:outline-none"
                  data-testid="configurator-diameter-input"
                />
                {conv && (
                  <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                    <div className="rounded bg-nibro-sage-pale px-2 py-1 font-mono font-bold text-zinc-900">DN{conv.dn}</div>
                    <div className="rounded bg-nibro-sage-pale px-2 py-1 font-mono font-bold text-zinc-900">{conv.inch}</div>
                  </div>
                )}
              </div>
              <Group label="Stripbreedte">
                {STRIP_WIDTHS.map((w) => (
                  <Pill key={w} active={stripWidth === w} onClick={() => setStripWidth(w)} testId={`strip-width-${w}`}>{w}mm</Pill>
                ))}
              </Group>
              <Group label="Strip dikte">
                {STRIP_THICKS.map((t) => (
                  <Pill key={t} active={stripThickness === t} onClick={() => setStripThickness(t)} testId={`strip-thick-${t}`}>{t}mm</Pill>
                ))}
              </Group>
            </Panel>

            <Panel title="Materiaal & Finish">
              <Group label="Materiaal">
                {MATERIALS.map((m) => (
                  <Pill key={m.v} active={material === m.v} onClick={() => setMaterial(m.v)} testId={`material-${m.v}`}>{m.label}</Pill>
                ))}
              </Group>
              <Group label="Afwerking">
                {FINISHES.map((f) => (
                  <Pill key={f.v} active={finish === f.v} onClick={() => setFinish(f.v)} testId={`finish-${f.v}`}>{f.label}</Pill>
                ))}
              </Group>
              <Group label="Rubber lining">
                {LININGS.map((l) => (
                  <Pill key={l.v} active={lining === l.v} onClick={() => setLining(l.v)} testId={`lining-${l.v}`}>{l.label}</Pill>
                ))}
              </Group>
            </Panel>
          </div>

          {/* CENTER: 3D viewer */}
          <div className="overflow-hidden rounded-md border-2 border-nibro-sage bg-white">
            <div className="aspect-square lg:aspect-auto lg:h-[640px]">
              <ClampViewer3D
                diameter={diameter}
                stripWidth={stripWidth}
                stripThickness={stripThickness}
                material={material}
                rubberLining={lining}
              />
            </div>
            <div className="border-t border-zinc-100 bg-nibro-sage-pale px-5 py-3">
              <div className="grid grid-cols-4 gap-3 text-center">
                <SmallStat label="Ø" value={`${diameter.toFixed(1)} mm`} />
                <SmallStat label="Strip" value={`${stripWidth}×${stripThickness}`} />
                <SmallStat label="Materiaal" value={material.toUpperCase()} />
                <SmallStat label="Klasse" value={loadClass} />
              </div>
            </div>
          </div>

          {/* RIGHT */}
          <div className="space-y-5">
            <Panel title="Montage & Belasting">
              <Group label="Montage">
                {MOUNTINGS.map((m) => (
                  <Pill key={m} active={mountingLabel === m} onClick={() => setMountingLabel(m)} testId={`mounting-${m}`}>{m}</Pill>
                ))}
              </Group>
              <Group label="Belastingsklasse">
                {LOAD_CLASSES.map((l) => (
                  <Pill key={l.v} active={loadClass === l.v} onClick={() => setLoadClass(l.v)} testId={`load-${l.v}`}>{l.label}</Pill>
                ))}
              </Group>
              <Group label="Lasoptie">
                {WELD_OPTS.map((w) => (
                  <Pill key={w.v} active={weldOpt === w.v} onClick={() => setWeldOpt(w.v)} testId={`weld-${w.v}`}>{w.label}</Pill>
                ))}
              </Group>
            </Panel>

            <div className="rounded-md border-2 border-nibro-sage bg-white p-5" data-testid="configurator-price-panel">
              <div className="flex items-center justify-between">
                <div className="label-uppercase flex items-center gap-1">
                  <Sparkles size={12} className="text-nibro-sage-dark" /> Live prijs
                </div>
                {loading && <Loader2 size={14} className="animate-spin text-nibro-sage-dark" />}
              </div>
              <div className="mt-3 font-display text-4xl font-bold text-zinc-900">
                €{price ? price.unit_price.toFixed(2) : "—"}
              </div>
              <div className="text-xs text-zinc-500">per stuk · excl. BTW</div>

              <div className="mt-4 flex items-center justify-between gap-3 border-t border-zinc-100 pt-4">
                <span className="text-sm text-zinc-600">Aantal</span>
                <input
                  type="number" min={1} value={quantity}
                  onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                  className="w-20 rounded-md border border-zinc-200 bg-white px-3 py-1.5 text-right font-mono text-sm focus:border-nibro-sage focus:outline-none"
                  data-testid="configurator-quantity"
                />
              </div>

              {price && (
                <div className="mt-3 space-y-1 text-xs text-zinc-500">
                  <Row label="Materiaal" value={`€${price.material_cost.toFixed(2)}`} />
                  <Row label="Productie" value={`€${price.production_cost.toFixed(2)}`} />
                  {price.finish_cost > 0 && <Row label="Afwerking" value={`€${price.finish_cost.toFixed(2)}`} />}
                  {price.lining_cost > 0 && <Row label="Lining" value={`€${price.lining_cost.toFixed(2)}`} />}
                  <Row label="Gewicht/stuk" value={`${price.weight_kg.toFixed(2)} kg`} />
                  <Row label="Levertijd" value={`${price.lead_time_days} dagen`} />
                </div>
              )}

              <div className="mt-4 border-t border-zinc-100 pt-3">
                <div className="flex items-baseline justify-between">
                  <span className="label-uppercase">Totaal ({quantity}x)</span>
                  <span className="font-display text-2xl font-bold text-nibro-sage-dark">
                    €{price ? price.total_price.toFixed(2) : "—"}
                  </span>
                </div>
              </div>

              <button
                type="button"
                onClick={handleAdd}
                disabled={!price}
                className="mt-4 flex w-full items-center justify-center gap-2 rounded-md bg-nibro-sage px-4 py-3 text-sm font-bold text-white transition hover:bg-nibro-sage-hover disabled:opacity-50"
                data-testid="configurator-add-to-cart"
              >
                <ShoppingCart size={16} /> Voeg maatwerk toe
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Panel({ title, children }) {
  return (
    <div className="rounded-md border border-zinc-200 bg-white p-5">
      <div className="label-uppercase mb-4">{title}</div>
      <div className="space-y-4">{children}</div>
    </div>
  );
}
function Group({ label, children }) {
  return (
    <div>
      <div className="label-uppercase mb-2">{label}</div>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}
function Pill({ active, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`rounded-md border px-3 py-1.5 text-xs font-semibold transition ${
        active
          ? "border-nibro-sage bg-nibro-sage text-white"
          : "border-zinc-200 bg-white text-zinc-600 hover:border-nibro-sage"
      }`}
    >
      {children}
    </button>
  );
}
function SmallStat({ label, value }) {
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-nibro-sage-dark">{label}</div>
      <div className="mt-0.5 font-mono text-sm font-bold text-zinc-900">{value}</div>
    </div>
  );
}
function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <span>{label}</span>
      <span className="font-mono text-zinc-700">{value}</span>
    </div>
  );
}
